import { useContentfulInspectorMode } from "@contentful/live-preview/react";
import type { ModuleQuoteFieldsFragment } from "./__generated/module-quote.contentful.generated";
import { Blockquote, BlockquoteFooter, BlockquoteQuote, Box, Grid, GridItem } from "@project/ui";
import { QuoteIcon } from "@/components/svgs/quote-icon";
import { ModuleRenderer } from "@/lib/contentful/modules/ModuleRenderer";
import {
    type MicrocopyInputType,
    useMicrocopy,
} from "@/lib/contentful/microcopy/microcopy-context";
import { useMemo } from "react";
import { ModuleSpacer } from "@/components/module-spacer";
import { gridGap, gridTemplateColumns } from "@project/ui/src/theme/global-styles";

export type ModuleQuoteProps = ModuleQuoteFieldsFragment & {
    microcopySets: MicrocopyInputType[];
    isEmbedded?: boolean;
};

export const ModuleQuote = (props: ModuleQuoteProps) => {
    const {
        microcopySets,
        sys: { id },
        quote,
        source,
        widget,
        isEmbedded,
    } = props;

    const { add: addMicrocopy } = useMicrocopy();
    if (microcopySets) {
        microcopySets.forEach((microcopySet) => addMicrocopy(microcopySet));
    }

    const inspectorMode = useContentfulInspectorMode({ entryId: id });

    const widgetIsHealthy = useMemo(() => {
        if (widget?.__typename === "ModuleAudioPlayer") {
            if (isEmbedded) return false;
            return !!widget.audioFile || !!widget.image;
        }
        return false;
    }, [isEmbedded, widget]);

    const { colStartLarge, colEndLarge } = useMemo(() => {
        let colStartLarge;
        let colEndLarge;

        if (widget && widgetIsHealthy) {
            colStartLarge = 5;
            colEndLarge = 10;
        } else if (isEmbedded) {
            colStartLarge = 1;
            colEndLarge = 13;
        } else {
            colStartLarge = 4;
            colEndLarge = 9;
        }
        return { colStartLarge, colEndLarge };
    }, [widget, widgetIsHealthy, isEmbedded]);

    return (
        <ModuleSpacer>
            <Grid
                templateColumns={gridTemplateColumns}
                templateRows={{ base: "repeat(2, auto)", l: "repeat(3, 1fr)" }}
                gap={gridGap}
                alignItems={"center"}
            >
                {widget && widgetIsHealthy ? (
                    <GridItem
                        className="item widget"
                        colStart={{ base: 1 }}
                        colEnd={{ base: 3, l: 5 }}
                        rowStart={{ base: 2, l: 1 }}
                        rowEnd={{ base: 4, l: 5 }}
                    >
                        <Box width={{ base: "100%", l: "80%" }}>
                            <ModuleRenderer
                                componentProps={{ ...widget, isEmbedded }}
                                key={widget?.sys?.id}
                            />
                        </Box>
                    </GridItem>
                ) : null}
                <GridItem
                    className="item quote"
                    colStart={{ base: 1, l: colStartLarge }}
                    colEnd={{ base: 3, l: colEndLarge }}
                    rowStart={{ base: 1, l: 1 }}
                    rowEnd={{ base: 2, l: 5 }}
                >
                    <Box>
                        <Box mb={6}>
                            <QuoteIcon />
                        </Box>
                        <Blockquote>
                            <BlockquoteQuote {...inspectorMode({ fieldId: "quote" })}>
                                {quote}
                            </BlockquoteQuote>
                            <BlockquoteFooter {...inspectorMode({ fieldId: "source" })}>
                                {source}
                            </BlockquoteFooter>
                        </Blockquote>
                    </Box>
                </GridItem>
            </Grid>
        </ModuleSpacer>
    );
};
